import InputAutocomplete from '@components/InputAutocomplete';
import InputAutocompleteAsync from '@components/InputAutocompleteAsync';
import InputBanner from '@components/InputBanner';
import InputField from '@components/InputField';
import InputLogo from '@components/InputLogo';
import InputPhotos from '@components/InputPhotos';
import InputRichText from '@components/InputRichText';
import InputVideos from '@components/InputVideos';
import { GET_PROS } from '@gql/pros';
import { FormControlLabel, FormGroup, FormLabel, Stack, Switch } from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { BrandFormValues, BrandItem } from './types';

interface BrandFormProps {
    formMode: 'create' | 'edit';
    editingBrand: BrandItem | null;
    optionsData?: {
        options?: {
            sectors?: Array<{
                children?: Array<{
                    id: string;
                    name: string;
                }>;
            }>;
        };
    };
}

const BrandForm: React.FC<BrandFormProps> = ({
    formMode,
    editingBrand,
    optionsData,
}) => {
    const formMethods = useFormContext<BrandFormValues>();

    return (
        <Stack spacing={2}>
            {formMode === 'create' ? (
                <InputAutocompleteAsync
                    id="proId"
                    name="proId"
                    size='medium'
                    query={GET_PROS}
                    getOptions={data =>
                        data?.getPros?.docs?.map(pro => ({
                            label: pro.commercialName,
                            value: pro._id,
                        })) || []
                    }
                    placeholder="Sélectionner un pro"
                />
            ) : (
                <InputField
                    label="Pro"
                    value={editingBrand?.pro?.commercialName || ''}
                    disabled
                />
            )}

            <Controller
                name="isFeatured"
                control={formMethods.control}
                render={({ field }) => (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={field.value}
                                onChange={event => field.onChange(event.target.checked)}
                            />
                        }
                        label="En vedette sur la page d'accueil"
                    />
                )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Controller
                    name="brandName"
                    control={formMethods.control}
                    rules={{ required: 'Le nom de la marque est requis' }}
                    render={({ field, fieldState }) => (
                        <InputField
                            {...field}
                            fullWidth
                            variant='outlined'
                            label="Nom de la marque"
                            placeholder="Nom de la marque"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
                <FormGroup sx={{ width: '100%' }}>
                    <FormLabel>
                        Secteur d'activité
                    </FormLabel>
                    <InputAutocomplete
                        name="sector"
                        placeholder="Secteur d'activité"
                        size='medium'
                        options={
                            optionsData?.options?.sectors?.[0]?.children?.map(item => ({
                                label: item.name,
                                value: item.id,
                            })) || []
                        }
                    />
                </FormGroup>
            </Stack>

            <InputRichText
                label="Description"
                control={formMethods.control}
                name="description"
                placeholder="Description"
                required
                maxLength={1000}
                fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <InputLogo
                    name="brandLogo"
                    label="Logo de la marque"
                    control={formMethods.control}
                    helpText="Formats acceptés: JPG, PNG (Max: 2MB, 800x800px)"
                    validation={{
                        maxSizeInMB: 2,
                        // maxWidth: 800,
                        // maxHeight: 800,
                        aspectRatio: 1
                    }}
                    existingFile={editingBrand?.brandLogo}
                />
                
                <InputBanner
                    name="brandBanner"
                    label="Bannière"
                    control={formMethods.control}
                    helpText="Formats acceptés: JPG, PNG (Max: 5MB, Ratio 16:9)"
                    validation={{
                        maxSizeInMB: 5,
                        // maxWidth: 1920,
                        // maxHeight: 1080,
                        aspectRatio: 16 / 9
                    }}
                    existingFile={editingBrand?.brandBanner}
                />
            </Stack>
            <InputPhotos
                name="brandPhotos"
                label="Photos de la marque"
                control={formMethods.control}
                helpText="Formats acceptés: JPG, PNG (Max: 3MB par image, 10 photos max)"
                validation={{
                    maxSizeInMB: 5,
                    // maxWidth: 1920,
                    // maxHeight: 1920,
                    maxCount: 10,
                    aspectRatio: 1
                }}
                existingFiles={editingBrand?.brandPhotos}
            />

            <InputVideos
                name="brandVideos"
                label="Vidéos de la marque"
                control={formMethods.control}
                helpText="Formats acceptés: MP4, WebM, MOV (Max: 50MB par vidéo, 5 vidéos max)"
                validation={{
                    maxSizeInMB: 50,
                    maxDurationSeconds: 300, // 5 minutes
                    maxCount: 5,
                    acceptedFormats: ['mp4', 'webm', 'mov']
                }}
                existingFiles={editingBrand?.brandVideos}
            />
        </Stack>
    );
};

export default BrandForm;
